// PricingDetails/DynamicPricingForm/DynamicPricingForm.jsx
import React, { useMemo, useCallback } from 'react';
import MaterialSelector from '../MaterialSelector/MaterialSelector';

// Create a memoized version of MaterialSelector
const MemoizedMaterialSelector = React.memo(MaterialSelector);

const DynamicPricingForm = ({ fields, values, onChange, onMaterialSelect }) => {

    // Helper to get nested value from object using path string
    const getNestedValue = (obj, path) => {
        if (!path) return undefined;
        return path.split('.').reduce((current, key) => {
            return current && current[key] !== undefined ? current[key] : undefined;
        }, obj);
    };

    // Helper to calculate price per meter for value additions
    const calculateValueAdditionPrice = useCallback((item) => {
        if (!item?._material || !item?.quantity) return 0;

        const price = item._material.selling_price_per_unit || 0;
        const quantity = item.quantity || 0;

        return price * quantity;
    }, []);

    const renderField = useCallback((field) => {
        // Get the value based on target if available, otherwise use field.name
        const value = field.target
            ? getNestedValue(values, field.target)
            : values[field.name];

        switch (field.type) {
            case 'material-select':
                const selectedMaterial = values[`_${field.name}`] || values[field.name] || null;

                return (
                    <div className="mb-3" key={field.name}>
                        <label className="form-label fw-medium">
                            {field.label} {field.required && <span className="text-danger">*</span>}
                        </label>
                        <MemoizedMaterialSelector
                            categorySlug={field.categorySlug}
                            value={selectedMaterial}
                            onChange={(material) => onMaterialSelect(field, material)}
                            filters={field.filters}
                            placeholder={`Search ${field.label.toLowerCase()}...`}
                            displayFields={field.displayFields}
                        />
                        {selectedMaterial && (
                            <div className="mt-2 p-2 bg-light rounded">
                                <small>
                                    <strong>Price:</strong> ₹{selectedMaterial.selling_price_per_unit}/{selectedMaterial.unit}
                                </small>
                                <br />
                                <small className="text-muted">
                                    {field.mappings?.map(m => {
                                        const mappedValue = getNestedValue(values, m.target);
                                        return mappedValue ? `${m.target}: ${mappedValue} ` : '';
                                    }).filter(Boolean).join(' | ')}
                                </small>
                            </div>
                        )}
                        {field.description && (
                            <small className="text-muted d-block mt-1">{field.description}</small>
                        )}
                    </div>
                );

            case 'select':
                return (
                    <div className="mb-3" key={`${field.name}${field.label}`}>
                        <label className="form-label fw-medium">
                            {field.label} {field.required && <span className="text-danger">*</span>}
                        </label>
                        <select
                            className="form-select"
                            value={value !== undefined ? value : field.default || ''}
                            onChange={(e) => onChange(field, e.target.value)}
                        >
                            <option value="">Select {field.label}</option>
                            {field.options.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label || opt.value}
                                </option>
                            ))}
                        </select>
                    </div>
                );

            case 'number':
                return (
                    <div className="mb-3" key={`${field.name}${field.label}`}>
                        <label className="form-label fw-medium">
                            {field.label} {field.required && <span className="text-danger">*</span>}
                        </label>
                        <input
                            type="number"
                            className="form-control"
                            value={value !== undefined ? value : field.default || ''}
                            onChange={(e) => {
                                const newValue = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                onChange(field, newValue);
                            }}
                            min={field.min}
                            max={field.max}
                            step={field.step || 1}
                            readOnly={field.readonly}
                        />
                        {field.description && (
                            <small className="text-muted">{field.description}</small>
                        )}
                    </div>
                );

            case 'text':
                return (
                    <div className="mb-3" key={`${field.name}${field.label}`}>
                        <label className="form-label fw-medium">
                            {field.label} {field.required && <span className="text-danger">*</span>}
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            value={value || ''}
                            onChange={(e) => onChange(field, e.target.value)}
                            placeholder={field.placeholder}
                            readOnly={field.readonly}
                        />
                        {field.description && (
                            <small className="text-muted">{field.description}</small>
                        )}
                    </div>
                );

            default:
                return null;
        }
    }, [values, onChange, onMaterialSelect]);

    const renderRepeater = useCallback((section) => {
        // Determine which array to use - raw data (_valueAdditions) or current data (valueAdditions)
        // For editing, we want to use the current data that's being updated
        const items = values[section.target] || [];

        const addItem = () => {
            const newItems = [...items, {}];
            onChange({ target: section.target }, newItems);
        };

        const updateItem = (index, field, fieldValue) => {
            const newItems = [...items];

            // Update the field value
            newItems[index] = { ...newItems[index], [field.name]: fieldValue };

            // If this is a material selection, store the material object and update unit
            if (field.name === 'material' && fieldValue) {
                newItems[index]._material = fieldValue; // Keep for compatibility
                newItems[index].unit = fieldValue.unit;
                delete(newItems[index]['material']);
            }

            onChange({ target: section.target }, newItems);
        };

        const removeItem = (index) => {
            const newItems = items.filter((_, i) => i !== index);
            onChange({ target: section.target }, newItems);
        };

        return (
            <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="fw-bold mb-0">{section.section}</h6>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={addItem}
                    >
                        <i className="bi bi-plus-circle me-1"></i>
                        Add {section.section}
                    </button>
                </div>

                {items.map((item, index) => {
                    // Use stored pricePerMeter if available, otherwise calculate
                    const displayPrice = (item._material && item.quantity ? calculateValueAdditionPrice(item) : null);

                    return (
                        <div key={`${section.target}-${index}`} className="card mb-3 border">
                            <div className="card-body">
                                <div className="d-flex justify-content-between mb-2">
                                    <h6 className="mb-0">
                                        {section.section} #{index + 1}
                                        {displayPrice !== null && (
                                            <span className="ms-3 badge bg-info">
                                                ₹{displayPrice.toFixed(2)}/meter
                                            </span>
                                        )}
                                    </h6>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => removeItem(index)}
                                    >
                                        <i className="bi bi-trash"></i>
                                    </button>
                                </div>
                                <div className="row">
                                    {section.repeaterFields.map(field => {
                                        // For material-select type in repeater
                                        if (field.type === 'material-select') {
                                            const materialValue = item._material;

                                            return (
                                                <div key={`${section.target}-${index}-${field.name}`} className={field.width || 'col-md-3'}>
                                                    <div className="mb-2">
                                                        <label className="form-label small">{field.label}</label>
                                                        <MemoizedMaterialSelector
                                                            categorySlug={field.categorySlug}
                                                            value={materialValue}
                                                            onChange={(material) => updateItem(index, field, material)}
                                                            placeholder={`Select ${field.label}`}
                                                            displayFields={field.displayFields}
                                                            filters={field.filters}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        }

                                        // Regular field types
                                        return (
                                            <div key={`${section.target}-${index}-${field.name}`} className={field.width || 'col-md-3'}>
                                                {field.type === 'select' ? (
                                                    <div className="mb-2">
                                                        <label className="form-label small">{field.label}</label>
                                                        <select
                                                            className="form-select form-select-sm"
                                                            value={item[field.name] || ''}
                                                            onChange={(e) => updateItem(index, field, e.target.value)}
                                                        >
                                                            <option value="">Select</option>
                                                            {field.options.map(opt => (
                                                                <option key={opt.value} value={opt.value}>
                                                                    {opt.label || opt.value}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                ) : (
                                                    <div className="mb-2">
                                                        <label className="form-label small">{field.label}</label>
                                                        <input
                                                            type={field.type === 'number' ? 'number' : 'text'}
                                                            className="form-control form-control-sm"
                                                            value={item[field.name] || ''}
                                                            onChange={(e) => {
                                                                const newValue = field.type === 'number'
                                                                    ? (e.target.value === '' ? 0 : parseFloat(e.target.value))
                                                                    : e.target.value;
                                                                updateItem(index, field, newValue);
                                                            }}
                                                            placeholder={field.placeholder}
                                                            min={field.min}
                                                            step={field.step}
                                                            readOnly={field.readonly}
                                                        />
                                                        {field.description && (
                                                            <small className="text-muted d-block">{field.description}</small>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    );
                })}

                {items.length === 0 && (
                    <p className="text-muted fst-italic">No {section.section.toLowerCase()} added yet.</p>
                )}
            </div>
        );
    }, [values, onChange, calculateValueAdditionPrice]);

    // Memoize the form sections
    const formSections = useMemo(() => {
        return fields.map((section, idx) => (
            <div key={idx} className="mb-5">
                <h5 className="border-bottom pb-2 mb-3">
                    <i className={`${section.sectionIcon} me-2`}></i>
                    {section.section}
                </h5>

                {section.isRepeater ? (
                    renderRepeater(section)
                ) : (
                    <div className="row">
                        {section.fields.map((field, index) => (
                            <div key={index} className="col-md-6">
                                {renderField(field)}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        ));
    }, [fields, renderField, renderRepeater]);

    return <div>{formSections}</div>;
};

export default DynamicPricingForm;